import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import {InterfaceFieldChangePropagationWarningQuery} from 'schemas/fields/__generated/InterfaceFieldChangePropagationWarningQuery';
import {FieldDefinition, FieldType} from 'schemas/fields/SchemaFieldConstants';
import {schemaFieldsetName} from 'schemas/schema-interface/util/schemaFieldsetName';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import ImmutableMap from 'util/ImmutableMap';

type SchemaInterfaceNode = ExtractByTypename<
  InterfaceFieldChangePropagationWarningQuery['schemaInterface'],
  'SchemaInterfaceNode'
>;

type SchemaNode = SchemaInterfaceNode['schemas'][number];

export interface FieldDefinitionChangeWarningProps {
  fromFieldDefinition: FieldDefinition;
  toFieldDefinition?: FieldDefinition;
  isMultiMoreStrict: boolean;
}

export interface FieldChangeWarningModalProps {
  onRequestSubmit: () => void;
  onRequestClose: () => void;
  content: JSX.Element;
}

export interface FieldRelaxationWarningProps {
  fromName: string;
  toName: string;
}

const SpecificWarningMessageByFieldDefinitionChangeMap = new ImmutableMap<
  {fromFieldDefinition: FieldDefinition; toFieldDefinition?: FieldDefinition},
  string
>([
  [
    {fromFieldDefinition: FieldType.DATETIME, toFieldDefinition: FieldType.DATE},
    'Changing a field\'s definition from "Datetime" to "Date" converts all datetime values in UTC time. ' +
      'E.g. a local datetime of 2020-01-01 9:00:00 PM, which corresponds to a datetime of 2020-01-02 6:00:00 AM in UTC time, ' +
      'becomes converted to a date of 2020-01-02.',
  ],
  [
    {fromFieldDefinition: FieldType.DATE, toFieldDefinition: FieldType.DATETIME},
    'Changing a field\'s definition from "Date" to "Datetime" converts all date values in UTC time. E.g. a ' +
      'local date of 2020-01-02 becomes converted to a datetime of 2020-01-02 00:00:00 AM in UTC time, which ' +
      'translates to a local datetime of 2020-01-01 05:00:00 PM.',
  ],
  [
    {fromFieldDefinition: FieldType.BLOB_LINK},
    'Changing a field\'s definition from "Attachment" may result in the loss of the linked files. If you need to ' +
      'retain these files, please download them before changing the field definition.',
  ],
  [
    {fromFieldDefinition: FieldType.TEXT, toFieldDefinition: FieldType.FLOAT},
    'Changing a field\'s definition from "Text" to "Decimal" will result in the loss of data if the data contains ' +
      'non-numeric characters. E.g. "5 mL" will not be converted and the data will be lost.',
  ],
  [
    {fromFieldDefinition: FieldType.TEXT, toFieldDefinition: FieldType.INTEGER},
    'Changing a field\'s definition from "Text" to "Integer" will result in the loss of data if the data contains ' +
      'non-numeric characters or any decimal values other than trailing zeros. E.g. "5 mL" or "1.23" will not be converted ' +
      'and the data will be lost.',
  ],
  [
    {fromFieldDefinition: FieldType.FLOAT, toFieldDefinition: FieldType.INTEGER},
    'Changing a field\'s definition from "Decimal" to "Integer" will result in the loss of data if the data contains any ' +
      'decimal values other than trailing zeros. E.g. 1.00 will be converted to 1, but 1.23 will not be converted and the ' +
      'data will be lost.',
  ],
  [
    {fromFieldDefinition: FieldType.TEXT, toFieldDefinition: FieldType.BIOENTITY_LINK},
    'Changing a field\'s definition from "Text" to "Entity" will result in loss of data in the warehouse ' +
      'for the values of this field. However, the original text values may still be displayed on the Benchling site.',
  ],
  [
    {fromFieldDefinition: FieldType.BIOENTITY_LINK, toFieldDefinition: FieldType.TEXT},
    'Changing a field\'s definition from "Entity" to "Text" will use the names of the entity values for the new text value. ' +
      'This is an irreversible process that cannot be undone by changing the field definition back to the original "Entity".',
  ],
  [
    {fromFieldDefinition: FieldType.SELECTOR, toFieldDefinition: FieldType.SELECTOR},
    'Changing a field\'s definition from "Dropdown A" to "Dropdown B" will result in loss of data in the ' +
      'warehouse for the values of this field. However, the original text values may still be displayed on the Benchling site.',
  ],
]);

const IS_MULTI_MORE_STRICT_WARNING_MESSAGE =
  'Changing a field\'s definition by deselecting "Multi-select" will result in loss of data ' +
  'in the warehouse for entities that have multiple values for this field. However, ' +
  'the original list of values may still be displayed on the site with an invalid label.';

function DataLossWarningBanner({message}: {message: string}): JSX.Element {
  return (
    <>
      <InlineBanner bannerType={InlineBannerType.WARNING} text={message} />
      <br />
    </>
  );
}

export default function FieldChangeWarningModal(props: FieldChangeWarningModalProps): JSX.Element {
  return (
    <Modal
      title="Confirm Changes"
      onRequestSubmit={props.onRequestSubmit}
      onRequestClose={props.onRequestClose}
      scrollable={true}
      medium={true}
      className="modal-text-only"
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Confirm', submit: true, style: ButtonStyle.NEGATIVE},
      ]}
    >
      {props.content}
    </Modal>
  );
}

export function FieldRelaxationWarning(props: FieldRelaxationWarningProps): JSX.Element {
  const DEFAULT_FROM_NAME = 'a specific schema';
  const DEFAULT_TO_NAME = 'any entity';
  return (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      text={`Expanding field from "${props.fromName ?? DEFAULT_FROM_NAME}" to "${
        props.toName ?? DEFAULT_TO_NAME
      }" is an irreversible operation.`}
    />
  );
}

export const INTERFACE_FIELD_CHANGE_PROPAGATION_WARNING_QUERY: DocumentNode = gql`
  query InterfaceFieldChangePropagationWarningQuery($interfaceId: StrictString!)
  @skipAudit
  @engComponent(name: SCHEMAS) {
    schemaInterface(id: $interfaceId) {
      ... on SchemaInterfaceNode {
        id
        schemas {
          id
          name
        }
      }
      ... on InaccessibleInfoNode {
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export function InterfaceFieldChangePropagationWarning(props: {interfaceId: string}): JSX.Element {
  const interfaceFieldChangePropagationWarningQueryResult =
    useBenchlingQuery<InterfaceFieldChangePropagationWarningQuery>(
      INTERFACE_FIELD_CHANGE_PROPAGATION_WARNING_QUERY,
      {
        variables: {interfaceId: props.interfaceId},
        fetchPolicy: 'network-only',
      }
    );
  let supportingInfo;
  if (interfaceFieldChangePropagationWarningQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Could not load affected schemas.');
    supportingInfo = null;
  } else if (interfaceFieldChangePropagationWarningQueryResult.type === QueryResultType.PENDING) {
    supportingInfo = 'Loading affected schemas...';
  } else if (interfaceFieldChangePropagationWarningQueryResult.type === QueryResultType.SUCCESS) {
    const schemaInterfaceNode = assertIsAccessible<SchemaInterfaceNode>(
      interfaceFieldChangePropagationWarningQueryResult.data.schemaInterface
    );
    if (schemaInterfaceNode.schemas.length === 0) {
      // This case ideally will never run. This means that the warning was rendered as a safety net because the
      // calling code either did not check if the interface had any schemas or wasn't able to finish checking.
      supportingInfo = null;
    } else {
      supportingInfo = `Schemas: ${schemaInterfaceNode.schemas
        .map((schema: SchemaNode) => schema.name)
        .join(', ')}.`;
    }
  }

  return (
    <InlineBanner
      bannerType={InlineBannerType.WARNING}
      text={`Changing this field will propagate changes to any schemas applying this ${schemaFieldsetName()}.`}
      supportingInfo={supportingInfo}
    />
  );
}

function getFieldChangeWarningMessage(fieldDefinitionChange: {
  fromFieldDefinition: FieldDefinition;
  toFieldDefinition?: FieldDefinition;
}): string | null {
  return (
    SpecificWarningMessageByFieldDefinitionChangeMap.get(fieldDefinitionChange) ||
    SpecificWarningMessageByFieldDefinitionChangeMap.get({
      fromFieldDefinition: fieldDefinitionChange.fromFieldDefinition,
    })
  );
}

export function shouldShowFieldChangeWarehouseWarning(
  multiChanged: boolean,
  fromFieldDefinition: FieldDefinition,
  toFieldDefinition?: FieldDefinition
): boolean {
  return (
    multiChanged ||
    (toFieldDefinition != null && // Null toFieldDefinition indicates no field definition change
      (fromFieldDefinition !== toFieldDefinition || // Show warning on any field definition change
        // Also show warning on any field definition "changes" explicitly listed in the change map
        // e.g. SELECTOR -> SELECTOR or BLOB_LINK -> <any>
        SpecificWarningMessageByFieldDefinitionChangeMap.has({fromFieldDefinition, toFieldDefinition}) ||
        SpecificWarningMessageByFieldDefinitionChangeMap.has({fromFieldDefinition})))
  );
}

export function FieldChangeWarehouseWarning(props: FieldDefinitionChangeWarningProps): JSX.Element {
  const fieldDefinitionChange = {
    fromFieldDefinition: props.fromFieldDefinition,
    toFieldDefinition: props.toFieldDefinition,
  };

  const fieldChangeDataLossWarningMessage = getFieldChangeWarningMessage(fieldDefinitionChange);
  // Prioritize fieldChangeDataLossWarningMessage as it contains more complete information which may not be
  // valid in tandem with IS_MULTI_MORE_STRICT_WARNING_MESSAGE.
  const shouldShowMultiWarning = fieldChangeDataLossWarningMessage == null && props.isMultiMoreStrict;

  return (
    <>
      {fieldChangeDataLossWarningMessage && (
        <DataLossWarningBanner message={fieldChangeDataLossWarningMessage} />
      )}
      {shouldShowMultiWarning && <DataLossWarningBanner message={IS_MULTI_MORE_STRICT_WARNING_MESSAGE} />}
      <div>
        <p>
          {fieldChangeDataLossWarningMessage || shouldShowMultiWarning
            ? 'Additionally, changing '
            : 'Changing '}
          a field's "Definition" or "Multi-select" value{' '}
          <b>will cause all data recorded for this field to be temporarily missing from the warehouse</b>{' '}
          while a backfill for these values is underway.
        </p>
        <p>
          We suggest triggering such changes in the evening or on a weekend to minimize disruption of
          warehouse usage.
        </p>
      </div>
    </>
  );
}
